import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { api, testTenantConnection, checkTenantProject, provisionTenantManual } from '../../lib/api';
import type { ProjectCheckResult } from '../../lib/api';

interface WaitlistUser {
  id: string;
  name: string;
  email: string;
  desired_site_name?: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function ProvisionWizard() {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Direct mode: no userId param means we're provisioning without a waitlist user
  const isDirectMode = !userId;

  const [waitlistUser, setWaitlistUser] = useState<WaitlistUser | null>(
    (location.state as { waitlistUser?: WaitlistUser })?.waitlistUser ?? null
  );
  const [isLoadingUser, setIsLoadingUser] = useState(!isDirectMode && !waitlistUser);

  // Appwrite mode: auto or manual
  const [appwriteMode, setAppwriteMode] = useState<'auto' | 'manual'>('auto');

  // Direct mode fields
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');

  // Manual Appwrite fields
  const [projectId, setProjectId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [connectionTested, setConnectionTested] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [projectCheck, setProjectCheck] = useState<ProjectCheckResult | null>(null);
  const [existingDataConfirmed, setExistingDataConfirmed] = useState(false);

  // Site config
  const [siteName, setSiteName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [domain, setDomain] = useState('');
  const [plan, setPlan] = useState('free');
  const [environment, setEnvironment] = useState('prod');
  const [tenantPassword, setTenantPassword] = useState('');

  const [provisioning, setProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState<{ ok: boolean; tenant?: any; error?: string } | null>(null);

  // Base domain for auto-generating domain field
  const [baseDomain, setBaseDomain] = useState('');

  useEffect(() => {
    api.get<{ base_domain?: string }>('/settings')
      .then(data => { if (data.base_domain) setBaseDomain(data.base_domain); })
      .catch(() => {});
  }, []);

  // Fetch waitlist user if not passed via state
  useEffect(() => {
    if (isDirectMode || waitlistUser) return;
    if (!userId) return;
    api.get<WaitlistUser[]>('/waitlist')
      .then(entries => {
        const found = entries.find(e => e.id === userId);
        if (found) setWaitlistUser(found);
      })
      .catch(() => {})
      .finally(() => setIsLoadingUser(false));
  }, [userId, waitlistUser, isDirectMode]);

  // Pre-fill site name from waitlist user
  useEffect(() => {
    if (waitlistUser?.desired_site_name && !siteName) {
      setSiteName(waitlistUser.desired_site_name);
      if (!slugManuallyEdited) {
        const newSlug = slugify(waitlistUser.desired_site_name);
        setSlug(newSlug);
        if (baseDomain) setDomain(`${newSlug}.${baseDomain}`);
      }
    }
  }, [waitlistUser, siteName, slugManuallyEdited, baseDomain]);

  function handleSiteNameChange(value: string) {
    setSiteName(value);
    if (!slugManuallyEdited) {
      const newSlug = slugify(value);
      setSlug(newSlug);
      if (baseDomain) setDomain(`${newSlug}.${baseDomain}`);
    }
  }

  function handleSlugChange(value: string) {
    setSlugManuallyEdited(true);
    const newSlug = slugify(value);
    setSlug(newSlug);
    if (baseDomain) setDomain(`${newSlug}.${baseDomain}`);
  }

  async function handleTestConnection() {
    setTestingConnection(true);
    setConnectionError('');
    setConnectionTested(false);
    setProjectCheck(null);
    setExistingDataConfirmed(false);
    try {
      const result = await testTenantConnection({
        appwrite_project_id: projectId,
        appwrite_api_key: apiKey,
      });
      if (result.ok) {
        setConnectionTested(true);
        try {
          const check = await checkTenantProject({
            appwrite_project_id: projectId,
            appwrite_api_key: apiKey,
          });
          setProjectCheck(check);
          if (!check.database_exists && !check.has_users) {
            setExistingDataConfirmed(true);
          }
        } catch {
          setExistingDataConfirmed(true);
        }
      } else {
        setConnectionError('Verbinding mislukt. Controleer de Project ID en API Key.');
      }
    } catch (e: unknown) {
      setConnectionError(e instanceof Error ? e.message : 'Verbinding testen mislukt.');
    } finally {
      setTestingConnection(false);
    }
  }

  async function handleProvision() {
    if (!isDirectMode && !waitlistUser) return;
    if (isDirectMode && !ownerEmail) return;
    setProvisioning(true);
    setProvisionResult(null);
    try {
      const basePayload = {
        name: siteName,
        slug,
        domain,
        plan,
        environment,
        tenant_admin_password: tenantPassword,
        // Only include Appwrite credentials in manual mode
        ...(appwriteMode === 'manual' ? {
          appwrite_project_id: projectId,
          appwrite_api_key: apiKey,
        } : {}),
      };

      const payload = isDirectMode
        ? { ...basePayload, owner_email: ownerEmail, owner_name: ownerName }
        : { ...basePayload, waitlist_user_id: waitlistUser!.id };

      const result = await provisionTenantManual(payload);
      setProvisionResult({ ok: true, tenant: result });
    } catch (e: unknown) {
      setProvisionResult({ ok: false, error: e instanceof Error ? e.message : 'Provisioning mislukt.' });
    } finally {
      setProvisioning(false);
    }
  }

  function canProvision(): boolean {
    const baseValid = siteName.trim().length > 0 && slug.trim().length > 0 && domain.trim().length > 0 && tenantPassword.length >= 8;
    if (isDirectMode && !ownerEmail.trim()) return false;
    if (appwriteMode === 'manual' && (!connectionTested || !existingDataConfirmed)) return false;
    return baseValid;
  }

  if (isLoadingUser) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Tenant Provisioning</h1>
        <div className="card p-8 text-center text-sm text-gray-400">Gebruiker laden...</div>
      </div>
    );
  }

  if (!isDirectMode && !waitlistUser) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Tenant Provisioning</h1>
        <div className="card p-8 text-center">
          <p className="text-sm text-gray-500 mb-4">Wachtlijst-gebruiker niet gevonden.</p>
          <Link to="/admin/waitlist" className="text-sm text-brand-600 font-medium hover:underline">
            Terug naar wachtlijst
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tenant Provisioning</h1>
        <p className="text-sm text-gray-500 mt-1">
          {isDirectMode ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="px-2 py-0.5 bg-brand-100 text-brand-700 text-xs font-medium rounded">Direct</span>
              Nieuwe tenant zonder wachtlijst
            </span>
          ) : (
            <>Voor <span className="font-medium text-gray-700">{waitlistUser!.name}</span> ({waitlistUser!.email})</>
          )}
        </p>
      </div>

      {/* Owner (direct mode) */}
      {isDirectMode && (
        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Eigenaar</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mailadres</label>
              <input type="email" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)} placeholder="jan@example.com" className="input w-full" />
              <p className="text-xs text-gray-400 mt-1">Hiermee logt de eigenaar in op het platform.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Naam</label>
              <input type="text" value={ownerName} onChange={e => setOwnerName(e.target.value)} placeholder="Jan de Fotograaf" className="input w-full" />
            </div>
          </div>
        </div>
      )}

      {/* Site Configuration */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Site configuratie</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sitenaam</label>
            <input type="text" value={siteName} onChange={e => handleSiteNameChange(e.target.value)} placeholder="Studio Jan" className="input w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input type="text" value={slug} onChange={e => handleSlugChange(e.target.value)} placeholder="studio-jan" className="input w-full" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Domein</label>
            <input type="text" value={domain} onChange={e => setDomain(e.target.value)} placeholder={baseDomain ? `naam.${baseDomain}` : 'naam.doorkoppen.nl'} className="input w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select value={plan} onChange={e => setPlan(e.target.value)} className="input w-full">
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="business">Business</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Omgeving</label>
            <select value={environment} onChange={e => setEnvironment(e.target.value)} className="input w-full">
              <option value="prod">Productie</option>
              <option value="test">Test</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Test routeert naar de test-deploy (test-branch)</p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Wachtwoord tenant admin</label>
          <input type="password" value={tenantPassword} onChange={e => setTenantPassword(e.target.value)} placeholder="Minimaal 8 tekens" className="input w-full" />
          <p className="text-xs text-gray-400 mt-1">Hiermee kan de eigenaar inloggen op de site. Geef dit door aan de gebruiker.</p>
        </div>
      </div>

      {/* Appwrite Configuration */}
      <div className="card p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Appwrite Project</h2>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setAppwriteMode('auto')}
            className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg border transition-colors ${
              appwriteMode === 'auto'
                ? 'bg-brand-50 border-brand-300 text-brand-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Automatisch aanmaken
            <p className="text-xs font-normal mt-0.5 opacity-70">Project en API key worden automatisch aangemaakt</p>
          </button>
          <button
            type="button"
            onClick={() => setAppwriteMode('manual')}
            className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg border transition-colors ${
              appwriteMode === 'manual'
                ? 'bg-brand-50 border-brand-300 text-brand-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            Handmatig opgeven
            <p className="text-xs font-normal mt-0.5 opacity-70">Gebruik een bestaand Appwrite project</p>
          </button>
        </div>

        {appwriteMode === 'auto' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
            Het Appwrite project <code className="bg-green-100 px-1 rounded text-xs">tenant-{slug || '...'}</code> wordt automatisch aangemaakt met alle benodigde collecties, buckets en API key.
          </div>
        )}

        {appwriteMode === 'manual' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project ID</label>
              <input type="text" value={projectId} onChange={e => setProjectId(e.target.value)} placeholder="bijv. 6478a3b2c1d4e" className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Key (secret)</label>
              <input type="password" value={apiKey} onChange={e => { setApiKey(e.target.value); setConnectionTested(false); setConnectionError(''); }} placeholder="Plak hier de API secret key" className="input w-full" />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleTestConnection}
                disabled={!apiKey.trim() || !projectId.trim() || testingConnection}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {testingConnection ? 'Testen...' : 'Verbinding testen'}
              </button>
              {connectionTested && (
                <span className="flex items-center gap-1 text-sm text-green-700">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Verbinding succesvol
                </span>
              )}
            </div>
            {connectionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{connectionError}</div>
            )}
            {connectionTested && projectCheck && (projectCheck.database_exists || projectCheck.has_users) && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                <h3 className="text-sm font-semibold text-amber-800">Dit project bevat al data</h3>
                <div className="text-sm text-amber-700 space-y-1">
                  {projectCheck.has_documents && (
                    <div>
                      <p>Collecties met documenten:</p>
                      <ul className="list-disc list-inside ml-2 mt-1">
                        {Object.entries(projectCheck.document_counts)
                          .filter(([, count]) => count > 0)
                          .map(([col, count]) => (
                            <li key={col}><code className="text-xs bg-amber-100 px-1 rounded">{col}</code>: {count} document(en)</li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {projectCheck.has_users && <p>{projectCheck.user_count} gebruiker(s) aanwezig</p>}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={existingDataConfirmed} onChange={e => setExistingDataConfirmed(e.target.checked)} className="rounded border-amber-400 text-amber-600 focus:ring-amber-500" />
                  <span className="text-sm font-medium text-amber-800">Ik begrijp dit en wil doorgaan</span>
                </label>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Provision button */}
      {!provisionResult && (
        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Bevestigen</h2>
          <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
            {!isDirectMode && <SummaryRow label="Eigenaar" value={`${waitlistUser!.name} (${waitlistUser!.email})`} />}
            {isDirectMode && ownerEmail && <SummaryRow label="Eigenaar" value={ownerName ? `${ownerName} (${ownerEmail})` : ownerEmail} />}
            <SummaryRow label="Site" value={siteName || '-'} />
            <SummaryRow label="Slug" value={slug || '-'} />
            <SummaryRow label="Domein" value={domain || '-'} />
            <SummaryRow label="Plan" value={plan.charAt(0).toUpperCase() + plan.slice(1)} />
            <SummaryRow label="Omgeving" value={environment === 'test' ? 'Test' : 'Productie'} />
            <SummaryRow label="Appwrite" value={appwriteMode === 'auto' ? `Automatisch (tenant-${slug})` : projectId} />
          </div>
          <button
            onClick={handleProvision}
            disabled={provisioning || !canProvision()}
            className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {provisioning ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Bezig met provisionen...
              </span>
            ) : (
              'Provisionen'
            )}
          </button>
        </div>
      )}

      {provisionResult?.ok && (
        <div className="card p-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-sm font-semibold text-green-800 mb-1">Tenant succesvol aangemaakt</h3>
            <p className="text-sm text-green-700 mb-3">De tenant is ingericht en klaar voor gebruik.</p>
            <Link to="/admin/tenants" className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors">
              Naar tenants
            </Link>
          </div>
        </div>
      )}

      {provisionResult && !provisionResult.ok && (
        <div className="card p-6">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-sm font-semibold text-red-800 mb-1">Provisioning mislukt</h3>
            <p className="text-sm text-red-700">{provisionResult.error}</p>
          </div>
        </div>
      )}

      {/* Cancel */}
      {!provisionResult?.ok && (
        <div className="flex justify-end">
          <Link to={isDirectMode ? '/admin/tenants' : '/admin/waitlist'} className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
            Annuleren
          </Link>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}
